import { USE_NATIVE_DRIVER } from '@shared-ui/platform/animations'
import { memo, useCallback, useEffect, useRef } from 'react'
import { Animated, Image, type ImageProps, Platform } from 'react-native'

const FADE_DURATION = 250

type FastImageProps = Omit<ImageProps, 'onLoad' | 'source'> & {
  onLoad?: ImageProps['onLoad']
  source?: ImageProps['source']
  fadeOnLoad?: boolean
}

const FastImageNative =
  Platform.OS !== 'web'
    ? (() => {
        try {
          return require('@d11/react-native-fast-image').default
        } catch {
          return null
        }
      })()
    : null

const AnimatedImage = Animated.createAnimatedComponent(Image)
const AnimatedFastImageNative = FastImageNative ? Animated.createAnimatedComponent(FastImageNative) : null

const loadedSources = new Set<string>()

function getSourceKey(source: FastImageProps['source']): string | null {
  if (!source) return null

  if (typeof source === 'number') return `asset:${source}`

  if (Array.isArray(source)) return source[0]?.uri ?? null

  if (typeof source === 'object' && source && 'uri' in source) return source.uri ?? null

  return null
}

export function isFastImageSourceLoaded(source: FastImageProps['source']): boolean {
  const sourceKey = getSourceKey(source)

  return Boolean(sourceKey && loadedSources.has(sourceKey))
}

export const FastImage = memo(function FastImageComponent({
  source,
  style,
  onLoad,
  onError,
  fadeOnLoad,
  ...props
}: FastImageProps) {
  const sourceKey = getSourceKey(source)
  const isAlreadyLoaded = isFastImageSourceLoaded(source)
  const opacity = useRef(new Animated.Value(fadeOnLoad && !isAlreadyLoaded ? 0 : 1)).current
  const hasNotifiedLoadRef = useRef(false)
  const loadStatusRef = useRef({ sourceKey, hasError: false })

  if (loadStatusRef.current.sourceKey !== sourceKey) {
    loadStatusRef.current = { sourceKey, hasError: false }
  }

  useEffect(() => {
    hasNotifiedLoadRef.current = false

    if (!fadeOnLoad || !sourceKey) return

    opacity.setValue(loadedSources.has(sourceKey) ? 1 : 0)
  }, [fadeOnLoad, opacity, sourceKey])

  // Помечаем source загруженным и анимируем opacity для fadeOnLoad.
  const markLoaded = useCallback(() => {
    if (hasNotifiedLoadRef.current) return

    hasNotifiedLoadRef.current = true

    if (sourceKey) loadedSources.add(sourceKey)

    if (fadeOnLoad) {
      Animated.timing(opacity, {
        toValue: 1,
        duration: FADE_DURATION,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start()
    }
  }, [fadeOnLoad, opacity, sourceKey])

  // onLoad: снимаем fade и пробрасываем колбэк наружу.
  const notifyLoaded = useCallback<NonNullable<ImageProps['onLoad']>>(
    (event) => {
      loadStatusRef.current.hasError = false
      markLoaded()
      onLoad?.(event)
    },
    [markLoaded, onLoad],
  )

  const notifyError = useCallback<NonNullable<ImageProps['onError']>>(
    (event) => {
      loadStatusRef.current.hasError = true
      onError?.(event)
    },
    [onError],
  )

  // onLoadEnd: fallback, если onLoad не пришёл (react-native-fast-image).
  const notifyLoadedEnd = useCallback(() => {
    if (hasNotifiedLoadRef.current || loadStatusRef.current.hasError) return

    markLoaded()
    onLoad?.({} as Parameters<NonNullable<ImageProps['onLoad']>>[0])
  }, [markLoaded, onLoad])

  if (!source) return null

  const defaultSource = Platform.OS === 'web' && isAlreadyLoaded && !Array.isArray(source) ? source : undefined
  const webInstantProps = defaultSource ? { defaultSource } : null

  if (!fadeOnLoad) {
    const StaticImage = FastImageNative ?? Image
    return (
      <StaticImage
        {...props}
        {...webInstantProps}
        source={source}
        style={style}
        onLoad={notifyLoaded}
        onError={notifyError}
        onLoadEnd={notifyLoadedEnd}
      />
    )
  }

  const AnimatedComponent = AnimatedFastImageNative ?? AnimatedImage
  return (
    <AnimatedComponent
      {...props}
      {...webInstantProps}
      source={source}
      style={[style, { opacity }]}
      onLoad={notifyLoaded}
      onError={notifyError}
      onLoadEnd={notifyLoadedEnd}
    />
  )
})
